import '../health-professional-modal.scss';

import { useMutation } from '@apollo/client';
import { kebabCase, omit } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProfessionalHonorificValues } from '../../../../data/data/health-professionals/select-values';
import { useHealthProfessionalHealthProviderAgendas } from '../../../../data/data/health-professionals/use-health-professional-health-provider-agendas';
import { useHealthProfessionalItem } from '../../../../data/data/health-professionals/use-health-professional-item';
import { useGenderValues } from '../../../../data/data/select-values';
import { toUpload } from '../../../../data/GraphQL/api/format';
import {
  ConsultType,
  Gender,
  GetHealthProfessionalItemsDocument,
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
  Honorific,
  Language,
  UpdateHealthProfessionalItemDocument,
  UpdateHealthProfessionalItemStatusDocument,
} from '../../../../data/types/api/graphql';
import { formatDate, maxBirthDate } from '../../../../data/utils/date.utils';
import {
  isEmptyOrNone,
  useFormatters,
} from '../../../../data/utils/formatters.utils';
import {
  FileSizeImage,
  FileTypeImage,
  getFileDataFromUrl,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { AvatarUploadController } from '../../../../elements/avatar/avatar-upload/controllers/avatar-upload';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { InputSelect } from '../../../../elements/form/input/input-select';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Spinner } from '../../../../elements/spinner/spinner';
import { Tag, TagType } from '../../../../elements/tag/tag';
import { Tooltip } from '../../../../elements/tooltip/tooltip';
import { CopyText } from '../../../../elements/utils/copy-text/copy-text';
import { useHealthProfessionalForm } from '../../form/use-health-professional-form';

export enum HealthProfessionalModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
}

type HealthProfessionalModalProps = {
  action?: HealthProfessionalModalAction;
  healthProfessionalId: string | null | undefined;
  onSaved?: (value: HealthProfessionalItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function HealthProfessionalModal({
  healthProfessionalId,
  onSaved,
  onCancel,
  onClose,
  ...props
}: HealthProfessionalModalProps) {
  const intl = useIntl();

  const { showAlertMessage, user } = useAppContext();

  const [action, setAction] = useState<HealthProfessionalModalAction>(
    HealthProfessionalModalAction.DEFAULT
  );

  // get healthproffesional data
  const {
    data: healthProfessionalItemData,
    loading: healthProfessionalItemLoading,
  } = useHealthProfessionalItem(healthProfessionalId);

  const { data: genderData } = useGenderValues();

  const { data: honorificData } = useHealthProfessionalHonorificValues();

  // init form
  const [formStep, setFormStep] = useState<number>(1);

  useEffect(() => {
    setAction(props.action ?? HealthProfessionalModalAction.DEFAULT);
  }, [props.action]);

  useEffect(() => {
    setFormStep(1);
  }, [action]);

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid, setErrors } = useHealthProfessionalForm();

  // load data
  useEffect(() => {
    if (healthProfessionalItemData) {
      form.reset(
        omit(
          {
            ...healthProfessionalItemData,
            meetingRoomLink: healthProfessionalItemData.meetingRoomLink ?? '',
            healthProviderId: healthProfessionalItemData.healthProvider?.id,
            consultTypes: healthProfessionalItemData.consultTypes.filter(
              option => option !== ConsultType.Physical
            ),
            initials: healthProfessionalItemData.initials ?? '',
          },
          'avatar'
        )
      );

      (async () => {
        form.setValue(
          'avatar',
          healthProfessionalItemData.avatar
            ? await getFileDataFromUrl(healthProfessionalItemData.avatar)
            : null
        );
      })();
    }
  }, [healthProfessionalItemData, form]);

  const healthProviderId = form.watch('healthProviderId');

  const {
    loading: practicesLoading,
    practices,
    update: updatePractice,
  } = useHealthProfessionalHealthProviderAgendas(
    healthProfessionalItemData,
    healthProfessionalItemLoading,
    healthProviderId
  );

  const [
    updateHealthProfessionalItem,
    { loading: updateHealthProfessionalItemLoading },
  ] = useMutation(UpdateHealthProfessionalItemDocument, {
    refetchQueries: [GetHealthProfessionalItemsDocument],
  });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      try {
        const result = await updateHealthProfessionalItem({
          variables: {
            ...values,
            firstName: values.firstName ?? '',
            lastName: values.lastName ?? '',
            email: values.email ?? '',
            initials: values.initials ?? '',
            phoneNumber: values.phoneNumber
              ? values.phoneNumber.replace(/\s/g, '')
              : '',
            meetingRoomLink: values.meetingRoomLink ?? '',
            id: healthProfessionalId ?? '',
            healthProviderId: healthProviderId ?? '',
            avatar: values.avatar
              ? {
                  fileName: values.avatar.name,
                  base64Data: toUpload(values.avatar.base64),
                }
              : {
                  deleteFile: true,
                },

            healthProviders: practices.map(option => ({
              healthProviderId: option.id,
              patientWeeksBefore: option.value,
            })),
            consultTypes: values.consultTypes.map(
              option => option as unknown as ConsultType
            ),
            isAdmin: values.isAdmin === true,
            language: Language.Nl,
          },
        });

        if (result.data?.healthProfessionalUpdate.healthProfessional) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'health-professional.health-professional-update.success',
            }),
          });

          onSaved?.(
            result.data
              .healthProfessionalUpdate as HealthProfessionalItemDataFragment
          );

          setAction(HealthProfessionalModalAction.DEFAULT);
        } else {
          setErrors(result.data?.healthProfessionalUpdate?.errors);
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    updateHealthProfessionalItem,
    healthProfessionalId,
    healthProviderId,
    practices,
    showAlertMessage,
    intl,
    onSaved,
    setErrors,
  ]);

  const [
    updateHealthProfessionalItemStatus,
    { loading: updateHealthProfessionalItemStatusLoading },
  ] = useMutation(UpdateHealthProfessionalItemStatusDocument, {
    refetchQueries: [GetHealthProfessionalItemsDocument],
  });

  const onHandleStatus = useCallback(async () => {
    try {
      let status: HealthProfessionalStatus | null = null;

      if (
        healthProfessionalItemData?.status === HealthProfessionalStatus.Active
      ) {
        status = HealthProfessionalStatus.Inactive;
      }

      if (
        healthProfessionalItemData?.status === HealthProfessionalStatus.Inactive
      ) {
        status = HealthProfessionalStatus.Active;
      }

      if (status) {
        const result = await updateHealthProfessionalItemStatus({
          variables: {
            id: healthProfessionalItemData?.id ?? '',
            status,
          },
        });

        if (result.data?.healthProfessionalStatus) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message:
              status === HealthProfessionalStatus.Inactive
                ? intl.formatMessage({
                    id: 'health-professional.deactivated',
                  })
                : intl.formatMessage({
                    id: 'health-professional.activated',
                  }),
          });
        }
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    healthProfessionalItemData,
    updateHealthProfessionalItemStatus,
    showAlertMessage,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action === HealthProfessionalModalAction.UPDATE) {
        if (!confirmed && form.formState.isDirty) {
          setCancelPromptOpen(true);
        } else {
          setAction(HealthProfessionalModalAction.DEFAULT);
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [action, form.formState.isDirty, onCancel, onClose]
  );

  const { getFullName, getBirthDate } = useFormatters();

  return (
    <>
      <Modal
        testId="health-professional"
        loading={healthProfessionalItemLoading}
        title={
          action !== HealthProfessionalModalAction.DEFAULT ? (
            <CustomFormattedMessage
              id={`health-professional.health-professional-${kebabCase(
                action
              )}.title`}
            />
          ) : (
            <CustomFormattedMessage id="health-professional.health-professional.title" />
          )
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          action === HealthProfessionalModalAction.UPDATE ? (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              {formStep > 1 && (
                <Button inverted onClick={() => onHandleFormStep(formStep - 1)}>
                  <CustomFormattedMessage id="common.previous" />
                </Button>
              )}

              {formStep < 2 ? (
                <Button onClick={() => onHandleFormStep(formStep + 1)}>
                  <CustomFormattedMessage id="common.next" />
                </Button>
              ) : (
                <Button
                  icon={
                    updateHealthProfessionalItemLoading || practicesLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={
                    !formIsValid ||
                    updateHealthProfessionalItemLoading ||
                    practicesLoading
                  }
                  onClick={onHandleSubmit}
                >
                  <CustomFormattedMessage id="health-professional.health-professional-update.save" />
                </Button>
              )}
            </>
          ) : (
            <>
              {healthProfessionalItemData?.status ===
                HealthProfessionalStatus.Active && (
                <Button
                  icon={
                    updateHealthProfessionalItemStatusLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={updateHealthProfessionalItemStatusLoading}
                  inverted
                  onClick={onHandleStatus}
                >
                  <CustomFormattedMessage id="health-professional.button.deactivate" />
                </Button>
              )}

              {healthProfessionalItemData?.status ===
                HealthProfessionalStatus.Inactive && (
                <Button
                  icon={
                    updateHealthProfessionalItemStatusLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={updateHealthProfessionalItemStatusLoading}
                  inverted
                  onClick={onHandleStatus}
                >
                  <CustomFormattedMessage id="health-professional.button.activate" />
                </Button>
              )}
              <Button
                onClick={() => setAction(HealthProfessionalModalAction.UPDATE)}
              >
                <CustomFormattedMessage id="health-professional.button.update" />
              </Button>
            </>
          )
        }
      >
        {action === HealthProfessionalModalAction.UPDATE ? (
          <FormProvider {...form}>
            {formStep === 1 && (
              <>
                <FormColumns>
                  <FormRow width="100px">
                    <AvatarUploadController
                      name="avatar"
                      fileTypes={FileTypeImage}
                      fileSize={FileSizeImage}
                    />
                  </FormRow>

                  <FormRow>
                    <InputFieldController
                      name="firstName"
                      label={
                        <>
                          <CustomFormattedMessage id="common.firstname" />
                          <small>*</small>
                        </>
                      }
                      placeholder={intl.formatMessage({
                        id: 'common.firstname',
                      })}
                    />
                  </FormRow>

                  <FormRow>
                    <InputFieldController
                      name="lastName"
                      label={
                        <>
                          <CustomFormattedMessage id="common.lastname" />
                          <small>*</small>
                        </>
                      }
                      placeholder={intl.formatMessage({
                        id: 'common.lastname',
                      })}
                    />
                  </FormRow>

                  <FormRow>
                    <InputFieldController
                      name="initials"
                      label={
                        <>
                          <CustomFormattedMessage id="common.initials" />
                          <small>*</small>
                        </>
                      }
                      placeholder={intl.formatMessage({
                        id: 'common.initials',
                      })}
                    />
                  </FormRow>
                </FormColumns>

                <FormRow>
                  <InputFieldController
                    name="email"
                    label={
                      <>
                        <CustomFormattedMessage id="common.email" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({ id: 'common.email' })}
                  />
                </FormRow>

                <FormRow>
                  <InputCheckboxController<Gender>
                    type={InputCheckboxType.RADIO}
                    name="gender"
                    label={
                      <>
                        <CustomFormattedMessage id="common.gender" />
                        <small>*</small>
                      </>
                    }
                    options={genderData}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    icon={BaseInputIcon.DATE}
                    type="date"
                    name="birthDate"
                    label={
                      <>
                        <CustomFormattedMessage id="common.birthdate" />
                        <small>*</small>
                      </>
                    }
                    max={maxBirthDate}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    type="phone"
                    name="phoneNumber"
                    label={
                      <>
                        <CustomFormattedMessage id="common.phone" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({
                      id: 'common.phone',
                    })}
                  />
                </FormRow>
              </>
            )}

            {formStep === 2 && (
              <>
                <FormRow>
                  <InputSelectController<string>
                    name="healthProviderId"
                    label={
                      <>
                        <CustomFormattedMessage id="health-professional.posting" />
                        <small>*</small>
                      </>
                    }
                    options={practices.map(option => ({
                      key: option.id,
                      title: option.name,
                    }))}
                  />
                </FormRow>

                <FormRow>
                  <InputSelectController<Honorific>
                    name="honorific"
                    label={
                      <>
                        <CustomFormattedMessage id="common.honorific" />
                        <small>*</small>
                      </>
                    }
                    placeholder={intl.formatMessage({
                      id: 'common.honorific-placeholder',
                    })}
                    options={honorificData}
                  />
                </FormRow>

                <FormRow>
                  <InputFieldController
                    name="meetingRoomLink"
                    label={<CustomFormattedMessage id="common.video-link" />}
                    placeholder={intl.formatMessage({
                      id: 'common.video-link',
                    })}
                  />
                </FormRow>

                <FormRow>
                  <InputCheckboxController<boolean>
                    name="isAdmin"
                    label={
                      <CustomFormattedMessage id="health-professional.is-admin" />
                    }
                    disabled={!user || healthProfessionalId === user.id}
                    options={[
                      {
                        key: true,
                        title: (
                          <CustomFormattedMessage id="health-professional.is-admin-placeholder" />
                        ),
                      },
                    ]}
                  />
                </FormRow>
                {practices?.length && (
                  <FormRow>
                    <div className="health-professional-agenda-table">
                      <div className="health-professional-agenda-table__header">
                        <div className="health-professional-agenda-table-item">
                          <div className="health-professional-agenda-table-item__column">
                            <p>
                              <CustomFormattedMessage id="health-professional.affiliated-practices" />
                            </p>
                          </div>
                          <div className="health-professional-agenda-table-item__column">
                            <p>
                              <CustomFormattedMessage id="health-professional.agendas.public" />
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="health-professional-agenda-table__content">
                        {practices.map(item => (
                          <div
                            key={item.id}
                            className="health-professional-agenda-table-item"
                          >
                            <div className="health-professional-agenda-table-item__tooltip">
                              {item.reasons.length > 0 && (
                                <Tooltip
                                  message={
                                    <>
                                      {item.reasons.map(reason => (
                                        <span key={reason}>{reason}</span>
                                      ))}
                                    </>
                                  }
                                >
                                  <i className="icon info" />
                                </Tooltip>
                              )}
                            </div>
                            <div className="health-professional-agenda-table-item__column">
                              {item.name}
                            </div>

                            {practicesLoading ? (
                              <div className="health-professional-agenda-table-item__loader">
                                <Spinner />
                              </div>
                            ) : (
                              <div className="health-professional-agenda-table-item__column">
                                <InputSelect<string>
                                  name="visible"
                                  options={[
                                    ...item.options.map(x => ({
                                      key: x.key,
                                      title: intl.formatMessage({
                                        id: x.title,
                                      }),
                                      disabled: x.disabled,
                                    })),
                                    ...Array.from(Array(52).keys()).map(
                                      option => ({
                                        key: `${option + 1}`,
                                        title: intl.formatMessage(
                                          {
                                            id:
                                              option + 1 === 1
                                                ? 'health-professional.agendas.week'
                                                : 'health-professional.agendas.weeks',
                                          },
                                          { count: option + 1 }
                                        ),
                                      })
                                    ),
                                  ]}
                                  value={`${item.value}`}
                                  onChange={val => {
                                    updatePractice(item.id, val);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </FormRow>
                )}
              </>
            )}
          </FormProvider>
        ) : (
          <div className="health-professional-modal-info">
            <div className="health-professional-modal-info__content">
              <h3>{getFullName(healthProfessionalItemData)}</h3>
              <p>
                <CustomFormattedMessage
                  id={`common.honorific.${kebabCase(
                    healthProfessionalItemData?.honorific
                  )}`}
                />
              </p>

              <h4>
                <CustomFormattedMessage id="health-professional.posting" />
              </h4>
              <p>
                {!isEmptyOrNone(
                  healthProfessionalItemData?.healthProvider?.name
                )
                  ? healthProfessionalItemData?.healthProvider?.name
                  : '-'}
              </p>

              <h4>
                <CustomFormattedMessage id="common.email" />
              </h4>
              <p>
                {healthProfessionalItemData?.email &&
                !isEmptyOrNone(healthProfessionalItemData?.email) ? (
                  <a
                    href={`mailto:${healthProfessionalItemData?.email}`}
                    title={healthProfessionalItemData?.email}
                  >
                    {healthProfessionalItemData?.email}
                  </a>
                ) : (
                  '-'
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="common.birthdate" />
              </h4>
              <p>
                {!isEmptyOrNone(healthProfessionalItemData?.birthDate) ? (
                  <CopyText
                    text={formatDate(
                      healthProfessionalItemData?.birthDate,
                      'dd-mm-YYYY'
                    )}
                  >
                    {getBirthDate(healthProfessionalItemData)}
                  </CopyText>
                ) : (
                  '-'
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="common.phone" />
              </h4>
              <p>
                {!isEmptyOrNone(healthProfessionalItemData?.phoneNumber)
                  ? healthProfessionalItemData?.phoneNumber
                  : '-'}
              </p>

              <h4>
                <CustomFormattedMessage id="common.video-link" />
              </h4>
              <p>
                {healthProfessionalItemData?.meetingRoomLink &&
                !isEmptyOrNone(healthProfessionalItemData?.meetingRoomLink) ? (
                  <a
                    href={healthProfessionalItemData?.meetingRoomLink}
                    target="_blank"
                    title={healthProfessionalItemData?.meetingRoomLink}
                    rel="noreferrer"
                  >
                    {healthProfessionalItemData?.meetingRoomLink}
                  </a>
                ) : (
                  '-'
                )}
              </p>

              <h4>
                <CustomFormattedMessage id="health-professional.affiliated-agendas" />
              </h4>
              <p>
                {healthProfessionalItemData?.agendas.filter(
                  x => x.patientWeeksBefore > -2
                ).length === 0
                  ? '-'
                  : healthProfessionalItemData?.agendas
                      .filter(x => x.patientWeeksBefore > -2)
                      .map((option, index) => (
                        <Fragment key={option.healthProviderId}>
                          {option.name}
                          {index <
                            healthProfessionalItemData?.agendas.length && (
                            <br />
                          )}
                        </Fragment>
                      ))}
              </p>
            </div>
            <div className="health-professional-modal-info__tags">
              {healthProfessionalItemData?.isAdmin && (
                <Tag type={TagType.ADMIN} />
              )}

              <Tag type={healthProfessionalItemData?.status} />
            </div>
          </div>
        )}
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
